import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { api } from '../lib/api.js';
import { ago } from '../lib/format.js';
import { Panel } from '../ui/index.js';
import { FoldTitle, usePanelFold } from './PanelFold.js';

/**
 * Who has opened this confidential ticket (072).
 *
 * Only on a ticket in a restricted queue, and only for the people who can
 * see it -- its members. HR will be asked who looked; this is the answer.
 */
export function TicketAccessLog({ tenant, number, enabled }: { tenant: string; number: number; enabled: boolean }) {
  const q = useQuery({
    queryKey: ['ticket-access-log', tenant, number],
    queryFn: () =>
      api.get<{ restricted: boolean; entries: { at: string; what: string; who: string }[] }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/access-log`,
      ),
    enabled,
  });
  const [open, toggle] = usePanelFold('access-log');
  if (!q.data?.restricted) return null;
  const entries = q.data.entries;
  const people = new Set(entries.map((e) => e.who)).size;
  return (
    <Panel
      title={
        <FoldTitle
          open={open}
          onToggle={toggle}
          title="Who has opened this"
          summary={<span className="text-ink-tertiary">{people} {people === 1 ? 'person' : 'people'} · {entries.length} views</span>}
        />
      }
    >
      {open && (
        <ul className="space-y-1">
          {entries.map((e, i) => (
            <li key={i} className="flex items-center gap-2 text-xs">
              <Eye aria-hidden className="h-3 w-3 shrink-0 text-ink-tertiary" />
              <span className="min-w-0 flex-1 truncate text-ink">{e.who}</span>
              <span className="shrink-0 text-2xs text-ink-tertiary">{ago(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
